/**
 * GrowthNudge — Dashboard card that appears when the user has completed
 * enough courses (≥3) AND already taken a `pre` FreedrIQ AND has NOT yet
 * taken the `post` version. Encourages them to take the post assessment to
 * unlock a personal growth report.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../App';
import { Sparkles, TrendingUp, ArrowRight, CheckCircle2 } from 'lucide-react';

const GrowthNudge = () => {
  const { API, user } = useContext(AuthContext);
  const [nudge, setNudge] = useState(null);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/freedriq/growth-nudge`)
      .then((r) => setNudge(r.data))
      .catch(() => setNudge(null));
  }, [API, user]);

  if (!nudge) return null;

  // Case A: has both pre and post → show the celebratory "growth completed" card
  if (nudge.has_pre && nudge.has_post) {
    return (
      <div className="pro-card p-4 border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-50 via-transparent to-transparent" data-testid="growth-nudge-completed">
        <div className="eyebrow text-emerald-700 inline-flex items-center gap-1.5">
          <CheckCircle2 className="w-3 h-3" /> Growth report ready
        </div>
        <div className="font-heading font-extrabold text-sm mt-1">
          You&apos;ve completed your {nudge.tier} pre + post FreedrIQ
        </div>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
          See the growth report and share your progress.
        </p>
        <Link
          to={`/freedriq/growth/${nudge.tier}`}
          className="btn-primary inline-flex items-center gap-1.5 rounded-full font-bold text-xs px-3 py-1.5 mt-3"
          data-testid="growth-nudge-view"
        >
          View report <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    );
  }

  // Case B: nudge to take the post assessment (only when show=true)
  if (!nudge.show) return null;

  return (
    <div className="pro-card p-4 border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent" data-testid="growth-nudge">
      <div className="eyebrow text-primary inline-flex items-center gap-1.5">
        <TrendingUp className="w-3 h-3" /> Growth check-in
      </div>
      <div className="font-heading font-extrabold text-sm mt-1">
        You&apos;ve completed {nudge.courses_completed} course{nudge.courses_completed === 1 ? '' : 's'}
      </div>
      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
        Retake the {nudge.tier} FreedrIQ to see how much your FreezeDryIQ Score has grown since your {nudge.pre_score}-point baseline.
      </p>
      <Link
        to={`/freedriq?tier=${nudge.tier}&phase=post`}
        className="btn-primary inline-flex items-center gap-1.5 rounded-full font-bold text-xs px-3 py-1.5 mt-3"
        data-testid="growth-nudge-take"
      >
        Take the post-assessment <Sparkles className="w-3 h-3" />
      </Link>
    </div>
  );
};

export default GrowthNudge;
